import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { listUsers, type UserItem } from '../api/users';
import { formatDate } from '../lib/format';
import {
  useUserActionMutations,
  type PendingAction,
} from './users/useUserActionMutations';
import { BulkActionBar } from './users/BulkActionBar';

// #579 — inline action buttons on each row. window.prompt collects
// the audit reason the server-side handlers require.
// #583 — leading checkbox column + BulkActionBar for multi-row cleanup.

interface RowActionsProps {
  u: UserItem;
  onSuspend: (id: string, reason: string) => void;
  onUnsuspend: (id: string, reason: string) => void;
  onDelete: (id: string, reason: string) => void;
  pendingAction: PendingAction | null;
}

const BTN_BASE =
  'px-2 py-1 text-xs font-medium rounded border border-border hover:bg-surface-subtle disabled:opacity-50 disabled:cursor-not-allowed';

function useRowHandlers(
  u: UserItem,
  onSuspend: (id: string, reason: string) => void,
  onUnsuspend: (id: string, reason: string) => void,
  onDelete: (id: string, reason: string) => void,
): { s: (e: React.MouseEvent) => void; u_: (e: React.MouseEvent) => void; d: (e: React.MouseEvent) => void } {
  const promptForReason = (verb: string): string | null => {
    const raw = window.prompt(`${verb} ${u.email}?\n\nReason (required for audit log):`);
    return raw && raw.trim() ? raw.trim() : null;
  };
  return {
    s: (e) => {
      e.stopPropagation();
      const r = promptForReason('Suspend');
      if (r) onSuspend(u.userId, r);
    },
    u_: (e) => {
      e.stopPropagation();
      const r = promptForReason('Unsuspend');
      if (r) onUnsuspend(u.userId, r);
    },
    d: (e) => {
      e.stopPropagation();
      if (!window.confirm(`DELETE ${u.email}?\n\nThis is irreversible — the user's DDB rows + S3 objects are purged.`)) return;
      const r = promptForReason('Delete');
      if (r) onDelete(u.userId, r);
    },
  };
}

function RowActions({ u, onSuspend, onUnsuspend, onDelete, pendingAction }: RowActionsProps): React.ReactElement {
  const h = useRowHandlers(u, onSuspend, onUnsuspend, onDelete);
  const disabled = pendingAction !== null;
  return (
    <div className="flex gap-1 justify-end">
      {u.suspended ? (
        <button type="button" onClick={h.u_} disabled={disabled} className={BTN_BASE} title="Lift suspension">
          {pendingAction === 'unsuspend' ? 'Unsuspending…' : 'Unsuspend'}
        </button>
      ) : (
        <button type="button" onClick={h.s} disabled={disabled} className={BTN_BASE} title="Block this user from calling the API">
          {pendingAction === 'suspend' ? 'Suspending…' : 'Suspend'}
        </button>
      )}
      <button type="button" onClick={h.d} disabled={disabled} className={`${BTN_BASE} text-red-700 hover:bg-red-50`} title="Irreversibly delete this user">
        {pendingAction === 'delete' ? 'Deleting…' : 'Remove'}
      </button>
    </div>
  );
}

interface UserRowProps {
  u: UserItem;
  onNavigate: (id: string) => void;
  onSuspend: (id: string, reason: string) => void;
  onUnsuspend: (id: string, reason: string) => void;
  onDelete: (id: string, reason: string) => void;
  pendingAction: PendingAction | null;
  isChecked: boolean;
  onToggleCheck: (id: string) => void;
}

export function UserRow({ u, onNavigate, onSuspend, onUnsuspend, onDelete, pendingAction, isChecked, onToggleCheck }: UserRowProps): React.ReactElement {
  return (
    <tr onClick={() => onNavigate(u.userId)} className="hover:bg-surface-subtle cursor-pointer">
      <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
        <input type="checkbox" aria-label={`Select ${u.email}`} checked={isChecked} onChange={() => onToggleCheck(u.userId)} className="h-4 w-4" />
      </td>
      <td className="px-4 py-3 font-mono text-xs text-text-muted">{u.userId.slice(0, 12)}…</td>
      <td className="px-4 py-3">{u.email}</td>
      <td className="px-4 py-3">{u.name ?? '—'}</td>
      <td className="px-4 py-3">{u.planTier ?? '—'}</td>
      <td className="px-4 py-3 text-text-muted">{formatDate(u.createdAt)}</td>
      <td className="px-4 py-3">
        {u.suspended ? (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Suspended</span>
        ) : (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">Active</span>
        )}
      </td>
      <td className="px-4 py-3">
        <RowActions u={u} onSuspend={onSuspend} onUnsuspend={onUnsuspend} onDelete={onDelete} pendingAction={pendingAction} />
      </td>
    </tr>
  );
}

const TH = 'px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide';

interface UsersTableProps {
  items: UserItem[];
  onNavigate: (id: string) => void;
  onSuspend: (id: string, reason: string) => void;
  onUnsuspend: (id: string, reason: string) => void;
  onDelete: (id: string, reason: string) => void;
  pendingActions: Record<string, PendingAction | null>;
  selected: Set<string>;
  onToggleCheck: (id: string) => void;
  onToggleAll: () => void;
}

export function UsersTable(p: UsersTableProps): React.ReactElement {
  const allChecked = p.items.length > 0 && p.items.every((u) => p.selected.has(u.userId));
  return (
    <div className="bg-white border border-border rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-surface-subtle border-b border-border">
          <tr>
            <th scope="col" className="px-4 py-3 text-left">
              <input type="checkbox" aria-label="Select all on this page" checked={allChecked} onChange={p.onToggleAll} className="h-4 w-4" />
            </th>
            <th scope="col" className={TH}>User ID</th>
            <th scope="col" className={TH}>Email</th>
            <th scope="col" className={TH}>Name</th>
            <th scope="col" className={TH}>Plan</th>
            <th scope="col" className={TH}>Created</th>
            <th scope="col" className={TH}>Status</th>
            <th scope="col" className={`${TH} text-right`}>Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {p.items.map((u) => (
            <UserRow
              key={u.userId}
              u={u}
              onNavigate={p.onNavigate}
              onSuspend={p.onSuspend}
              onUnsuspend={p.onUnsuspend}
              onDelete={p.onDelete}
              pendingAction={p.pendingActions[u.userId] ?? null}
              isChecked={p.selected.has(u.userId)}
              onToggleCheck={p.onToggleCheck}
            />
          ))}
          {p.items.length === 0 && (
            <tr>
              <td colSpan={8} className="px-4 py-8 text-center text-text-muted">No users found.</td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

interface HeaderProps {
  search: string;
  onSearchChange: (v: string) => void;
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
}

function UsersHeader({ search, onSearchChange, onSubmit }: HeaderProps): React.ReactElement {
  return (
    <div className="flex items-center justify-between">
      <h1 className="text-xl font-semibold text-text-primary">Users</h1>
      <form onSubmit={onSubmit} className="flex gap-2">
        <input
          type="search"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Search by email or name…"
          className="px-3 py-1.5 text-sm border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-brand-500 w-60"
        />
        <button type="submit" className="px-3 py-1.5 text-sm bg-brand-600 text-white rounded-md hover:bg-brand-700">Search</button>
      </form>
    </div>
  );
}

function useSelection(pageIds: string[]): {
  selected: Set<string>;
  toggle: (id: string) => void;
  toggleAll: () => void;
  clear: () => void;
} {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const toggle = (id: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = (): void => {
    setSelected((prev) => {
      const allOn = pageIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allOn) pageIds.forEach((id) => next.delete(id));
      else pageIds.forEach((id) => next.add(id));
      return next;
    });
  };
  const clear = (): void => setSelected(new Set());
  return { selected, toggle, toggleAll, clear };
}

export function UsersList(): React.ReactElement {
  const [search, setSearch] = useState('');
  const [cursor, setCursor] = useState<string | undefined>();
  const navigate = useNavigate();
  const actions = useUserActionMutations();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['users', { cursor, search }],
    queryFn: () => listUsers({ cursor, search: search || undefined, limit: 50 }),
    staleTime: 30_000,
  });
  const items = data?.items ?? [];
  const sel = useSelection(items.map((u) => u.userId));
  const handleSearch = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    setCursor(undefined);
  };
  return (
    <div className="space-y-4">
      <UsersHeader search={search} onSearchChange={setSearch} onSubmit={handleSearch} />
      <BulkActionBar selected={sel.selected} users={items} mutations={actions} onClear={sel.clear} />
      {isLoading && <p className="text-sm text-text-muted">Loading users…</p>}
      {isError && <p className="text-sm text-red-600">Failed to load users.</p>}
      {!isLoading && (
        <UsersTable
          items={items}
          onNavigate={(id) => navigate(`/users/${id}`)}
          onSuspend={actions.suspend}
          onUnsuspend={actions.unsuspend}
          onDelete={actions.delete}
          pendingActions={actions.pendingActions}
          selected={sel.selected}
          onToggleCheck={sel.toggle}
          onToggleAll={sel.toggleAll}
        />
      )}
      <div className="flex gap-3">
        {cursor && (<button onClick={() => setCursor(undefined)} className="text-sm text-brand-600 hover:underline">← First page</button>)}
        {data?.nextCursor && (<button onClick={() => setCursor(data.nextCursor ?? undefined)} className="text-sm text-brand-600 hover:underline">Next page →</button>)}
      </div>
    </div>
  );
}
